import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ChartRangeOptions } from '@core/models';
import {
  DayPerQuarterHourTopologicalTelemetryModel,
  DayPerHourTopologicalTelemetryModel,
  MonthPerDayTopologicalTelemetryModel,
  WeekPerDayTopologicalTelemetryModel,
  YearPerMonthTopologicalTelemetryModel,
} from '@core/models/topological-telemetry.model';
import { InsightsService } from '@core/services/insights.service';
import { InstallationService } from '@core/services/installation.service';
import { Installation } from '@core/models/installation.model';
import { displayZone, FALLBACK_TIME_ZONE } from '@core/helpers/display-time.helper';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { environment } from '@root/environments/environment';
import { catchError, map, of, pipe, switchMap, tap } from 'rxjs';

type TopologicalDeviceTelemetryState = {
  isLoadingDeviceTelemetry: boolean;
  deviceChartTimeOption: ChartRangeOptions;
  selectedDate: string;
  deviceTopologicalTelemetryYearPerMonthData: YearPerMonthTopologicalTelemetryModel | null;
  deviceTopologicalTelemetryMonthPerDayData: MonthPerDayTopologicalTelemetryModel | null;
  deviceTopologicalTelemetryWeekPerDayData: WeekPerDayTopologicalTelemetryModel | null;
  deviceTopologicalTelemetryDayPerHourData: DayPerHourTopologicalTelemetryModel | null;
  deviceTopologicalTelemetryDayPerQuarterHourData: DayPerQuarterHourTopologicalTelemetryModel | null;
  // The energyflow route answered 404: the device is visible, but its telemetry is not shared with
  // the user's organisation (a partnership whose type lacks view_device_telemetry).
  deviceTelemetryNotShared: boolean;
  // Zone the device's days are shown in: the installation's, or the fallback when it is unknown.
  timeZone: string;
  timeZoneIsFallback: boolean;
};

const today = new Date();

const initialState: TopologicalDeviceTelemetryState = {
  isLoadingDeviceTelemetry: false,
  deviceChartTimeOption: ChartRangeOptions.Month,
  selectedDate: `month-per-day/${today.getFullYear()}/${today.getMonth() + 1}`,
  deviceTopologicalTelemetryYearPerMonthData: null,
  deviceTopologicalTelemetryMonthPerDayData: null,
  deviceTopologicalTelemetryWeekPerDayData: null,
  deviceTopologicalTelemetryDayPerHourData: null,
  deviceTopologicalTelemetryDayPerQuarterHourData: null,
  deviceTelemetryNotShared: false,
  timeZone: FALLBACK_TIME_ZONE,
  timeZoneIsFallback: true,
};

export const TopologicalDeviceTelemetryStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  environment.storeWithDevTools('topological-telemetry'),
  withMethods(
    (
      store,
      insightsService = inject(InsightsService),
      installationService = inject(InstallationService),
    ) => ({
      // The zone of the installation the device with this LDN feeds. A lookup the API refuses or that
      // fails keeps the fallback zone.
      loadTimeZone: rxMethod<string | undefined>(
        pipe(
          switchMap(ldn =>
            ldn
              ? installationService.list({ ldn, limit: 10, offset: 0 }).pipe(
                  map(response => response?.installations ?? []),
                  catchError(() => of<Installation[]>([])),
                )
              : of<Installation[]>([]),
          ),
          tap(installations => {
            const { zone, fallback } = displayZone(installations.map(i => i.timezone));
            patchState(store, { timeZone: zone, timeZoneIsFallback: fallback });
          }),
        ),
      ),
      getDeviceTopologicalTelemetry: rxMethod<{
        deviceId: string;
        chartRange: ChartRangeOptions;
        selectedDate: string;
      }>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoadingDeviceTelemetry: true,
            }),
          ),
          switchMap(({ deviceId, chartRange, selectedDate }) => {
            return insightsService.getDeviceTopologicalTelemetryByTime(deviceId, selectedDate).pipe(
              tapResponse({
                next: (data: any) => {
                  patchState(store, { deviceTelemetryNotShared: false });
                  switch (chartRange) {
                    case ChartRangeOptions.DayPerQuarter:
                      return patchState(store, {
                        deviceTopologicalTelemetryDayPerQuarterHourData: data,
                        isLoadingDeviceTelemetry: false,
                      });
                    case ChartRangeOptions.Day:
                      return patchState(store, {
                        deviceTopologicalTelemetryDayPerHourData: data,
                        isLoadingDeviceTelemetry: false,
                      });
                    case ChartRangeOptions.Week:
                      return patchState(store, {
                        deviceTopologicalTelemetryWeekPerDayData: data,
                        isLoadingDeviceTelemetry: false,
                      });
                    case ChartRangeOptions.Month:
                      return patchState(store, {
                        deviceTopologicalTelemetryMonthPerDayData: data,
                        isLoadingDeviceTelemetry: false,
                      });
                    default:
                      patchState(store, {
                        deviceTopologicalTelemetryYearPerMonthData: data,
                        isLoadingDeviceTelemetry: false,
                      });
                  }
                },
                error: (error: unknown) => {
                  patchState(store, {
                    deviceTelemetryNotShared:
                      error instanceof HttpErrorResponse && error.status === 404,
                  });
                  switch (chartRange) {
                    case ChartRangeOptions.DayPerQuarter:
                      return patchState(store, {
                        deviceTopologicalTelemetryDayPerQuarterHourData: null,
                        isLoadingDeviceTelemetry: false,
                      });
                    case ChartRangeOptions.Day:
                      return patchState(store, {
                        deviceTopologicalTelemetryDayPerHourData: null,
                        isLoadingDeviceTelemetry: false,
                      });
                    case ChartRangeOptions.Week:
                      return patchState(store, {
                        deviceTopologicalTelemetryWeekPerDayData: null,
                        isLoadingDeviceTelemetry: false,
                      });
                    case ChartRangeOptions.Month:
                      return patchState(store, {
                        deviceTopologicalTelemetryMonthPerDayData: null,
                        isLoadingDeviceTelemetry: false,
                      });
                    default:
                      patchState(store, {
                        deviceTopologicalTelemetryYearPerMonthData: null,
                        isLoadingDeviceTelemetry: false,
                      });
                  }
                },
              }),
            );
          }),
        ),
      ),
      setDate: (date: string) => {
        return patchState(store, { selectedDate: date });
      },
      setRange: (range: ChartRangeOptions) => {
        // Only switch the range here. The date picker owns the reference date and
        // re-emits it (via setDate) encoded for the new range, keeping the shown
        // date and the fetched data in sync.
        return patchState(store, { deviceChartTimeOption: range });
      },
    }),
  ),
);
